export type PlanModeEntry = {
  entryType: string;
  userMessage?: { planMode?: boolean | null } | null;
  toolCall?: {
    toolName?: string | null;
    completedAt?: number | null;
    isError?: boolean | null;
    output?: unknown;
  } | null;
};

export type PlanModePreferences = {
  planModeDefault?: string | null;
  planModeChatDefault?: string | null;
  planModeWorkflowDefault?: string | null;
  planModeBranchDefault?: string | null;
} | null;

export function resolveDefaultPlanMode(
  preferences: PlanModePreferences | undefined,
  surface: "chat" | "workflow" | "branch"
): boolean {
  const value =
    surface === "chat"
      ? preferences?.planModeChatDefault
      : surface === "workflow"
        ? preferences?.planModeWorkflowDefault
        : preferences?.planModeBranchDefault;
  if (value != null) return value === "on";
  return preferences?.planModeDefault === "on";
}

// An active plan-mode session must win over surface defaults and saved state, or the toggle can silently drop mid-session and let the AI build while the user believes plan mode is still on.
export function resolveSeededPlanMode(args: {
  hasHistory: boolean;
  historyPlanMode: boolean;
  savedPlanMode: boolean | null;
  defaultPlanMode: boolean;
  surface: "chat" | "workflow" | "branch" | "none";
}): boolean {
  const {
    hasHistory,
    historyPlanMode,
    savedPlanMode,
    defaultPlanMode,
    surface,
  } = args;
  if (hasHistory) return historyPlanMode;
  if (surface === "branch") return defaultPlanMode;
  return savedPlanMode ?? defaultPlanMode;
}

function planModeStorageKey(chatId: string) {
  return `chat-plan-mode:${chatId}`;
}

export function loadPlanMode(chatId: string | null): boolean | null {
  if (!chatId) return null;
  try {
    const value = localStorage.getItem(planModeStorageKey(chatId));
    if (value === "true") return true;
    if (value === "false") return false;
    return null;
  } catch {
    return null;
  }
}

export function savePlanMode(chatId: string | null, planMode: boolean) {
  if (!chatId) return;
  try {
    localStorage.setItem(
      planModeStorageKey(chatId),
      planMode ? "true" : "false"
    );
  } catch {}
}

export function resolvePlanModeFromEntries(
  entries: readonly PlanModeEntry[]
): boolean {
  const latestUserIndex = entries.findLastIndex(
    (entry) => entry.entryType === "UserMessage" && entry.userMessage
  );
  const latestUser = latestUserIndex >= 0 ? entries[latestUserIndex] : null;
  if (!latestUser?.userMessage?.planMode) return false;

  for (const entry of entries.slice(latestUserIndex + 1)) {
    const toolCall = entry.toolCall;
    if (
      entry.entryType !== "AssistantToolUse" ||
      toolCall?.toolName !== "approvePlan" ||
      toolCall.completedAt == null ||
      toolCall.isError
    ) {
      continue;
    }
    const output = toolCall.output;
    if (
      output &&
      typeof output === "object" &&
      !Array.isArray(output) &&
      (output as { approved?: unknown }).approved === true
    ) {
      return false;
    }
  }

  return true;
}
